import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import * as d3 from 'd3';
import { useGetEvaluationsPresignedUrlMutation, useGetPdfLinkForConversationsMutation, useGetPartsZipComponentDetailsMutation, useGetPartsHotspotViewMutation } from '@circuitry-ai/doc-data';
import CtySpinner from '../spinner/CtySpinner';
import { Icon } from '@iconify/react';
import { Card, CardBody, CardHeader, CircularProgress } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { ZoomControls, type ViewTabOption } from './ZoomControls';
import { CartActions } from '../utils/cartActions';
import PartsImageViewer from './partsimageviewer';

interface PartsSvgViewerProps {
  svgUrl: string;
  isFileViewer?: boolean;
  currentActivePartsRowData?: any;
  setSelectedRowId?: any;
  activeSourceData?: any;
  filename?: string;
  partsListFromFileViewer?: any[]
  fileType?: string
  componentDetails?: any[]
  catalogSourceId?: string
  partsRowSelectToken?: number;
  // Multi-image nav: only rendered when the component has more than one diagram.
  onPrevImage?: () => void;
  onNextImage?: () => void;
  canGoPrevImage?: boolean;
  canGoNextImage?: boolean;
  // "Component Group" / "Component Name" segmented toggle, rendered inline in the
  // zoom-controls toolbar. Only rendered when provided.
  viewTabs?: ViewTabOption[];
  activeViewTabKey?: string;
  onViewTabChange?: (key: string) => void;
}

// Loading spinner component
const LoadingSpinner = ({ isFileViewer }: { isFileViewer: boolean | undefined }) => (
  <div className={`flex justify-center items-center ${isFileViewer ? 'h-[100vh]' : 'h-full'}`}>
    <CtySpinner size="lg" color="primary" />
  </div>
);

function ensureSvgOverflowVisible(svgText: string): string {
  const svgTagRegex = /<svg[^>]*>/;
  const match = svgTagRegex.exec(svgText);

  if (match) {
    const svgTag = match[0];
    if (!/overflow=["']visible["']/.test(svgTag)) {
      const updatedSvgTag = svgTag.replace(/<svg/, '<svg overflow="visible"');
      return svgText.replace(svgTag, updatedSvgTag);
    }
  }
  return svgText;
}

/**
 * Part tooltip payload for `TooltipComponent`.
 * - SVG viewer: set `anchorClientX` / `anchorClientY` (viewport).
 * - Image viewer: set `x` / `y` (relative to the offset parent where `TooltipComponent` is mounted).
 */
export interface PartInfo {
  parts_number: string;
  description: string;
  anchorClientX?: number;
  anchorClientY?: number;
  x?: number;
  y?: number;
  ref_number?: string;
  component_name?: string;
  part_file_name?: string;
}

const PARTS_CATALOG_S3_PREFIX = 'parts_catalog';

export function buildPartCatalogS3Key(
  catalogSourceId: string,
  fileName: string
): string {
  return `${PARTS_CATALOG_S3_PREFIX}/${catalogSourceId.trim()}/${fileName.trim()}`;
}

/**
 * Strip any directory prefix off a part image file name.
 *
 * Some screens send a prefixed name (the advisor table sends "images/row3_39.jpeg"
 * where BOM sends "row3_39.jpeg") while the object sits directly under
 * `parts_catalog/<source id>/`. Used only as a *retry* after the name as given
 * fails to resolve, so catalogs whose keys legitimately contain a directory keep
 * working untouched.
 */
export const toPartImageFileNameOnly = (value: string) =>
  value.split(/[/\\]/).filter(Boolean).pop() ?? '';

const normFileNameField = (value: unknown) =>
  value == null ? '' : String(value).trim();

const IMAGE_FILE_EXTENSION_RE = /\.(png|jpe?g|gif|webp|bmp|svg|tiff?)$/i;

/**
 * Field names that can carry a part's image. A parts row's own `file_name` *is*
 * its image (e.g. "row1_35.jpeg"); the other names are alternates the payload has
 * used. Tried in this order.
 */
const PART_IMAGE_FIELDS = [
  'part_file_name',
  'part_image_name',
  'part_image_file_name',
  'part_image',
  'file_name',
];

/**
 * Resolve the image to show in a part tooltip.
 *
 * A parts row's own `file_name` is its image; the other names are alternates the
 * payload has used. The tooltip presigns it as
 * `parts_catalog/<catalog source id>/<file name>`.
 */
export function getPartImageFields(
  detail?: any,
  fallbackPartFileName?: string | null
): Pick<PartInfo, 'part_file_name'> {
  let fileName = '';
  if (detail && typeof detail === 'object') {
    for (const field of PART_IMAGE_FIELDS) {
      const value = detail[field];
      // Only plain strings: an object or element here would stringify to junk like
      // "[object Object]" and be presigned as if it were a file name.
      if (typeof value !== 'string') continue;
      fileName = normFileNameField(value);
      if (fileName) break;
    }
  }
  if (!fileName) {
    // Row-derived fallbacks come from table cells, which hold a file name only on
    // the parts-catalog tables — elsewhere a cell can carry arbitrary content (even
    // a React element), so require it to look like an image file before using it.
    const fallback = normFileNameField(fallbackPartFileName);
    if (IMAGE_FILE_EXTENSION_RE.test(fallback)) {
      fileName = fallback;
    }
  }
  if (!fileName) {
    return {};
  }

  return { part_file_name: fileName };
}

const normPartField = (value: unknown) =>
  value == null || typeof value === 'object' ? '' : String(value).trim();

/**
 * Resolve the selected part row uniquely when multiple rows share a referenceNumber.
 * Prefer source_id / file_name, then ref + part # + name.
 */
export function findMatchingComponentDetail(
  details: any[] | undefined,
  selected: any | null | undefined
): any | undefined {
  if (!details?.length || !selected) return undefined;

  const selectedSourceId = normPartField(selected.source_id);
  if (selectedSourceId) {
    const bySourceId = details.find((entry) => normPartField(entry?.source_id) === selectedSourceId);
    if (bySourceId) return bySourceId;
  }

  const selectedFileName = normPartField(selected.file_name ?? selected.part_file_name);
  if (selectedFileName) {
    const byFileName = details.find((entry) => normPartField(entry?.file_name) === selectedFileName);
    if (byFileName) return byFileName;
  }

  const selectedRef = normPartField(selected.referenceNumber ?? selected.ref_number);
  const selectedPartsNumber = normPartField(selected.parts_number);
  const selectedName = normPartField(selected.name ?? selected.description);

  if (selectedRef || selectedPartsNumber || selectedName) {
    const byUniqueFields = details.find((entry) => {
      const entryRef = normPartField(entry?.referenceNumber);
      const entryPartsNumber = normPartField(entry?.parts_number);
      const entryName = normPartField(entry?.name);
      const refMatches = !selectedRef || entryRef === selectedRef;
      const partsMatches = !selectedPartsNumber || entryPartsNumber === selectedPartsNumber;
      const nameMatches = !selectedName || entryName === selectedName;
      return refMatches && partsMatches && nameMatches;
    });
    if (byUniqueFields) return byUniqueFields;
  }

  if (selectedRef) {
    return details.find((entry) => normPartField(entry?.referenceNumber) === selectedRef);
  }

  return undefined;
}

/**
 * The part selected in a parts table or on a hotspot, keyed by field name.
 * Chat and the parts-catalog viewer both pass it as `currentActivePartsRowData[0]`,
 * so viewers never depend on table column order.
 */
export interface SelectedPartRow {
  component_group?: string;
  component_name?: string;
  referenceNumber?: string;
  parts_number?: string;
  name?: string;
  quantity?: string;
  file_name?: string;
  part_file_name?: string;
  source_id?: string;
}

export function getSelectedPartRow(rowData: unknown): SelectedPartRow | null {
  const row = Array.isArray(rowData) ? rowData[0] : rowData;
  return row && typeof row === 'object' ? (row as SelectedPartRow) : null;
}

/** Row id shared by the chat parts table and diagram clicks to mark the active row. */
export function getSelectedPartRowId(part: SelectedPartRow | null | undefined): string {
  if (!part) return '';
  return [
    normPartField(part.component_name) || normPartField(part.component_group),
    normPartField(part.referenceNumber),
    normPartField(part.parts_number),
  ]
    .filter(Boolean)
    .join('-');
}

/**
 * Catalog detail for the selected part. Several parts can share one reference
 * number, so when the row has a part number the detail must have the same one.
 */
export function findSelectedPartDetail(
  details: any[] | undefined,
  row: SelectedPartRow | null | undefined
): any | undefined {
  if (!details?.length || !row) return undefined;
  const partsNumber = normPartField(row.parts_number);
  const match = findMatchingComponentDetail(details, row);
  if (!partsNumber || normPartField(match?.parts_number) === partsNumber) return match;
  return details.find((entry) => normPartField(entry?.parts_number) === partsNumber);
}

/**
 * Build the tooltip payload for the currently selected part.
 *
 * Shared by the SVG and image viewers (and the no-diagram fallback panel) so the
 * tooltip carries the same part number / description / part image regardless of
 * whether a hotspot was found on the diagram.
 */
export function buildPartInfoFromRowData(
  rowData: unknown,
  componentDetails?: any[]
): PartInfo | null {
  const row = getSelectedPartRow(rowData);
  if (!row) return null;
  const refNumber = normPartField(row.referenceNumber);
  const partsNumber = normPartField(row.parts_number);
  if (!refNumber && !partsNumber) return null;
  // Fall back to the row itself so it keeps its own file_name / part image.
  const match = findSelectedPartDetail(componentDetails, row) ?? row;
  return {
    parts_number: partsNumber || normPartField(match.parts_number),
    description: normPartField(row.name) || normPartField(match.name),
    // The clicked row keeps its own callout. The catalog match supplies the
    // image, and its reference only when the row has none.
    ref_number: refNumber || normPartField(match.referenceNumber),
    component_name: normPartField(row.component_name) || undefined,
    ...getPartImageFields(match, row.part_file_name),
  };
}

/** Hotspot entry from an answer's `element_refs[].text`, in `SelectedPartRow` shape. */
function fromAnswerPartEntry(entry: any): SelectedPartRow {
  const info = entry?.part_info ?? {};
  return {
    component_name: info.component_name ?? info.componentName,
    referenceNumber: info.reference_number ?? entry?.reference_number,
    parts_number: info.parts_number,
    name: info.description,
    quantity: info.quantity,
    part_file_name: info.part_file_name,
  };
}

/**
 * Part behind a diagram click: the selected table row when the click came from the
 * table, otherwise the hotspot entry (a catalog detail in the parts-catalog viewer,
 * an answer `element_refs` entry in chat).
 */
function resolveClickedPart(
  tableRowData: unknown,
  hotspotPart: any,
  isFileViewer: boolean | undefined
): SelectedPartRow {
  if (tableRowData) return getSelectedPartRow(tableRowData) ?? {};
  if (isFileViewer) return hotspotPart ?? {};
  return fromAnswerPartEntry(hotspotPart);
}

type SVGElementWithCTM = SVGGraphicsElement & { getScreenCTM(): DOMMatrix | null };

function pickLargestBBoxTextNode(nodes: SVGTextElement[]): SVGTextElement {
  let best = nodes[0];
  let bestArea = -Infinity;
  for (const n of nodes) {
    try {
      const b = n.getBBox();
      const a = b.width * b.height;
      if (a > bestArea) {
        bestArea = a;
        best = n;
      }
    } catch {
      // ignore
    }
  }
  return best;
}

function getTextTooltipAnchorClient(
  textNode: SVGTextElement,
  svgEl: SVGSVGElement
): { clientX: number; clientY: number } {
  try {
    const bbox = textNode.getBBox();
    const pt = svgEl.createSVGPoint();
    pt.x = bbox.x + bbox.width / 2;
    pt.y = bbox.y + bbox.height / 2;
    const ctm = (textNode as SVGElementWithCTM).getScreenCTM?.();
    if (ctm) {
      const scr = pt.matrixTransform(ctm);
      return { clientX: scr.x, clientY: scr.y };
    }
  } catch {
    // getBBox can throw
  }
  const tRect = textNode.getBoundingClientRect();
  return {
    clientX: tRect.left + tRect.width / 2,
    clientY: tRect.top + tRect.height / 2,
  };
}

interface SVGElementWithMethods extends SVGElement {
  getScreenCTM(): DOMMatrix | null;
  getBBox(): DOMRect;
}

const PartsSvgViewer: React.FC<PartsSvgViewerProps> = ({
  svgUrl,
  isFileViewer,
  currentActivePartsRowData,
  setSelectedRowId,
  activeSourceData,
  filename,
  partsListFromFileViewer,
  componentDetails,
  catalogSourceId,
  partsRowSelectToken,
  onPrevImage,
  onNextImage,
  canGoPrevImage,
  canGoNextImage,
  viewTabs,
  activeViewTabKey,
  onViewTabChange,
}) => {
  const partsListInline = useMemo(() => {
    return activeSourceData?.element_refs?.[0]?.text ?? [];
  }, [activeSourceData]);
  const partsList = isFileViewer ? partsListFromFileViewer : partsListInline;
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const tooltipSourceTextRef = useRef<SVGTextElement | null>(null);
  const syncTooltipAnchorRef = useRef<() => void>(() => {});
  const syncRafRef = useRef(0);
  const [tooltipData, setTooltipData] = useState<PartInfo | null>(null);
  const [svgLoadError, setSvgLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Reset tooltip when SVG URL changes
  useEffect(() => {
    setTooltipData(null);
  }, [svgUrl]);

  const activeReferenceNumber = getSelectedPartRow(currentActivePartsRowData)?.referenceNumber;

  const normalizeRefKey = useCallback((value: unknown) => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).trim();
    }
    return '';
  }, []);

  const syncTooltipAnchor = useCallback(() => {
    const node = tooltipSourceTextRef.current;
    const svg = svgRef.current;
    if (!node || !svg) return;
    let clientX: number;
    let clientY: number;
    try {
      ({ clientX, clientY } = getTextTooltipAnchorClient(node, svg));
    } catch {
      return;
    }
    setTooltipData((prev) => {
      if (!prev) return null;
      const px = prev.anchorClientX ?? 0;
      const py = prev.anchorClientY ?? 0;
      if (Math.abs(px - clientX) < 0.5 && Math.abs(py - clientY) < 0.5) return prev;
      return { ...prev, anchorClientX: clientX, anchorClientY: clientY };
    });
  }, []);

  syncTooltipAnchorRef.current = syncTooltipAnchor;

  useEffect(() => {
    if (!tooltipData) tooltipSourceTextRef.current = null;
  }, [tooltipData]);

  /** At most one anchor sync per frame (resize / scroll / observer). */
  const scheduleTooltipAnchorSync = useCallback(() => {
    if (syncRafRef.current) return;
    syncRafRef.current = requestAnimationFrame(() => {
      syncRafRef.current = 0;
      syncTooltipAnchorRef.current();
    });
  }, []);

  useEffect(() => {
    if (!tooltipData) return;
    const el = svgContainerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => scheduleTooltipAnchorSync());
    ro.observe(el);
    const onScrollResize = () => scheduleTooltipAnchorSync();
    globalThis.addEventListener('scroll', onScrollResize, true);
    globalThis.addEventListener('resize', onScrollResize);

    return () => {
      ro.disconnect();
      globalThis.removeEventListener('scroll', onScrollResize, true);
      globalThis.removeEventListener('resize', onScrollResize);
      if (syncRafRef.current) {
        cancelAnimationFrame(syncRafRef.current);
        syncRafRef.current = 0;
      }
    };
  }, [tooltipData?.parts_number, tooltipData?.ref_number, scheduleTooltipAnchorSync]);

  const handleTextClick = useCallback(
    (
      textElement: d3.Selection<SVGTextElement, unknown, any, any>,
      part: any,
      isTableRowClicked: boolean
    ) => {
      if (!textElement.node() || !svgRef.current || !svgContainerRef.current) return;

      const selectedPart = resolveClickedPart(
        isTableRowClicked ? currentActivePartsRowData : null,
        part,
        isFileViewer
      );
      const toolTipData: PartInfo = {
        parts_number: normPartField(selectedPart.parts_number),
        description: normPartField(selectedPart.name),
        ref_number: normPartField(selectedPart.referenceNumber),
        component_name: normPartField(selectedPart.component_name),
      };

      // The parts-catalog viewer's row is already the catalog detail.
      const imageDetail = isFileViewer
        ? selectedPart
        : findSelectedPartDetail(componentDetails, selectedPart);
      Object.assign(toolTipData, getPartImageFields(imageDetail, selectedPart.part_file_name));

      const node = textElement.node();
      if (!node || !svgRef.current) return;

      tooltipSourceTextRef.current = node;
      const { clientX, clientY } = getTextTooltipAnchorClient(node, svgRef.current);
      toolTipData.anchorClientX = clientX;
      toolTipData.anchorClientY = clientY;

      if (isFileViewer && !isTableRowClicked) {
        setSelectedRowId?.(part);
      } else if (!isFileViewer) {
        setSelectedRowId?.(getSelectedPartRowId(selectedPart));
      }

      setTooltipData({ ...toolTipData });
    },
    [currentActivePartsRowData, componentDetails, isFileViewer, setSelectedRowId]
  );

  function drawHighlightCircle(textElement: d3.Selection<SVGTextElement, unknown, any, any>, part?: any) {
    const node = textElement.node();
    if (!node?.parentNode || !svgRef.current) return;

    const bbox = (node as unknown as SVGGraphicsElement).getBBox();
    const parent = node.parentNode as SVGGElement | SVGElement;
    const parentCTM = (parent as SVGElementWithMethods)?.getScreenCTM?.();
    const textCTM = (node as SVGElementWithMethods).getScreenCTM();
    
    let cx: number;
    let cy: number;
    let r: number;
    
    if (textCTM && parentCTM && svgRef.current) {
      const point = svgRef.current.createSVGPoint();
      point.x = bbox.x + bbox.width / 2;
      point.y = bbox.y + bbox.height / 2;
      
      const screenPoint = point.matrixTransform(textCTM);
      const parentPoint = screenPoint.matrixTransform(parentCTM.inverse());
      
      cx = parentPoint.x;
      cy = parentPoint.y;
      
      const textScale = Math.hypot(textCTM.a, textCTM.b);
      const parentScale = Math.hypot(parentCTM.a, parentCTM.b);
      r = (Math.max(bbox.width, bbox.height) / 2 + 2) * (parentScale / textScale);
    } else {
      cx = bbox.x + bbox.width / 2;
      cy = bbox.y + bbox.height / 2;
      r = Math.max(bbox.width, bbox.height) / 2 + 2;
    }

    const circle = d3.select(parent)
      .insert('circle', () => node as any)
      .attr('class', 'highlight-circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', r)
      .style('pointer-events', 'all')
      .style('cursor', 'pointer');

    if (part) {
      circle.on('click', (event) => {
        event.stopPropagation();
        handleTextClick(textElement, part, false);
      });

      textElement
        .on('mouseenter', () => circle.classed('circle-hovered', true))
        .on('mouseleave', () => circle.classed('circle-hovered', false));
    }
  }

  /**
   * Anchor the tooltip to the centre of the diagram panel. Used when the selected
   * row has no matching hotspot on the diagram — the part may still have its own
   * image, so the tooltip must stay reachable instead of being suppressed.
   */
  const showCenteredTooltip = useCallback(() => {
    const container = svgContainerRef.current;
    if (!container) return;
    const info = buildPartInfoFromRowData(currentActivePartsRowData, componentDetails);
    if (!info) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    tooltipSourceTextRef.current = null;
    setTooltipData({
      ...info,
      anchorClientX: rect.left + rect.width / 2,
      anchorClientY: rect.top + rect.height / 2,
    });
  }, [currentActivePartsRowData, isFileViewer, componentDetails]);

  const updatePartsHighlighting = useCallback(() => {
    if (!svgRef.current) return;

    const svgElement = d3.select<SVGSVGElement, unknown>(svgRef.current);
    svgElement.selectAll('circle.highlight-circle').remove();
    const textElements = svgElement.selectAll<SVGTextElement, unknown>('text, g text');
    textElements.on('click', null).classed('highlighted', false);

    const findPart = (refNumber: string) => {
      const key = normalizeRefKey(refNumber);
      return isFileViewer
        ? partsList.find((p: any) => normalizeRefKey(p.referenceNumber) === key)
        : partsList.find((p: any) => normalizeRefKey(p.reference_number) === key);
    };

    textElements.each(function () {
      const textElement = d3.select<SVGTextElement, unknown>(this);
      const textContent = textElement.text().trim();
      if (!textContent) return;

      const part = findPart(textContent);
      if (part) {
        textElement
          .classed('highlighted', true)
          .on('click', (event) => {
            event.stopPropagation();
            handleTextClick(textElement, part, false);
          });
        drawHighlightCircle(textElement, part);
      }
    });

    if (activeReferenceNumber != null && String(activeReferenceNumber).trim() !== '') {
      const refKey = normalizeRefKey(activeReferenceNumber);
      const matchingNodes: SVGTextElement[] = [];
      textElements.each(function () {
        const el = this as SVGTextElement;
        if (normalizeRefKey(d3.select(el).text().trim()) === refKey) {
          matchingNodes.push(el);
        }
      });

      let anchoredToHotspot = false;
      if (matchingNodes.length > 0) {
        const bestNode = pickLargestBBoxTextNode(matchingNodes);
        const targetTextElement = d3.select<SVGTextElement, unknown>(bestNode);
        targetTextElement.style('fill', '#ff4500').style('font-weight', 'bold');
        const part = findPart(String(activeReferenceNumber));
        if (part) {
          drawHighlightCircle(targetTextElement as any, part);
          handleTextClick(targetTextElement as any, part, true);
          anchoredToHotspot = true;
        }
      }

      // No hotspot for this row on this diagram: still show the tooltip (centred
      // in the panel) so the part-level image stays accessible.
      if (!anchoredToHotspot) {
        showCenteredTooltip();
      }
    }
  }, [
    partsList,
    activeReferenceNumber,
    currentActivePartsRowData,
    handleTextClick,
    isFileViewer,
    normalizeRefKey,
    showCenteredTooltip,
  ]);

  // Single effect: both [partsList, isLoading] and [currentActivePartsRowData] previously called
  // updatePartsHighlighting separately; sharing the same dependency on updatePartsHighlighting
  // caused two runs (and double handleTextClick for table selection) when row data changed.
  useEffect(() => {
    if (isLoading) return;
    updatePartsHighlighting();
  }, [partsList, isLoading, currentActivePartsRowData, partsRowSelectToken, updatePartsHighlighting]);

  useEffect(() => {
    const loadSvg = async () => {
      setIsLoading(true);
      setSvgLoadError(null);

      if (!svgUrl) {
        setSvgLoadError('No SVG URL provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(svgUrl);
        const svgText = await response.text();
        const updatedSvgText = ensureSvgOverflowVisible(svgText);

        const parser = new DOMParser();
        const svgDocument = parser.parseFromString(
          updatedSvgText,
          'image/svg+xml'
        );
        const svgElement =
          svgDocument.documentElement as unknown as SVGSVGElement;

        if (svgContainerRef.current) {
          svgContainerRef.current.innerHTML = '';
          svgContainerRef.current.appendChild(svgElement);

          // Get the SVG's bounding box
          const bbox = svgElement.getBBox();

          // Add some padding (e.g., 5%)
          const padding = 0.05;
          const width = bbox.width * (1 + padding * 2);
          const height = bbox.height * (1 + padding * 2);
          const x = bbox.x - (bbox.width * padding);
          const y = bbox.y - (bbox.height * padding);

          // Set viewBox to match content bounds
          svgElement.setAttribute(
            "viewBox",
            `${x} ${y} ${width} ${height}`
          );

          // Set width and height to 100%
          svgElement.setAttribute("width", "100%");
          svgElement.setAttribute("height", "100%");

          // Add preserveAspectRatio to ensure proper scaling
          svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
        }

        svgRef.current = svgElement;


        const svgSelection = d3.select<SVGSVGElement, unknown>(svgElement);
        const zoomGroup = svgSelection.append('g');

        svgSelection.selectAll(':scope > *:not(g)').each(function () {
          const node = this as Node;
          zoomGroup.node()?.appendChild(node);
        });

        svgSelection
          .selectAll(':scope > g')
          .filter(function () {
            return this !== zoomGroup.node();
          })
          .each(function () {
            const node = this as Node;
            zoomGroup.node()?.appendChild(node);
          });

        const zoom = d3
          .zoom<SVGSVGElement, unknown>()
          .scaleExtent([0.1, 200])
          .on('zoom', (event) => {
            zoomGroup.attr('transform', event.transform);
            scheduleTooltipAnchorSync();
          });

        svgSelection.call(zoom);

        // Add background click handler to close tooltip
        // We use an overlay rect instead of the SVG itself to prevent conflicts with component clicks
        const svgBounds = svgElement.getBBox();
        svgSelection.insert('rect', ':first-child')
          .attr('x', svgBounds.x - svgBounds.width * 0.1)
          .attr('y', svgBounds.y - svgBounds.height * 0.1)
          .attr('width', svgBounds.width * 1.2)
          .attr('height', svgBounds.height * 1.2)
          .attr('fill', 'transparent')
          .style('pointer-events', 'all')
          .on('click', (event) => {
            // In D3 v6+, the event is passed as the first parameter
            // Only close tooltip if the click was directly on the background
            if (event.target === event.currentTarget) {
              setTooltipData(null);
            }
          });

        // Keep initial state at identity (same as refresh action).
        // This avoids hidden/zero-size container races on first render in conversation panel.
        zoomRef.current = zoom;
      } catch (error) {
        console.error('Error loading SVG:', error);
        setSvgLoadError(
          error instanceof Error ? error.message : 'Unknown error loading SVG'
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadSvg();
    return () => undefined;
  }, [scheduleTooltipAnchorSync, svgUrl]);

  const handleZoomIn = () => {
    if (svgRef.current && zoomRef.current) {
      d3.select<SVGSVGElement, unknown>(svgRef.current)
        .transition()
        .duration(200)
        .call(zoomRef.current.scaleBy, 1.2);
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current && zoomRef.current) {
      d3.select<SVGSVGElement, unknown>(svgRef.current)
        .transition()
        .duration(200)
        .call(zoomRef.current.scaleBy, 0.8);
    }
  };

  const handleReset = useCallback(() => {
    if (svgRef.current && zoomRef.current) {
      d3.select<SVGSVGElement, unknown>(svgRef.current)
        .transition()
        .duration(200)
        .call(zoomRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  // Add debounced resize handler
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        if (svgRef.current && zoomRef.current && svgContainerRef.current) {
          const svgElement = d3.select<SVGSVGElement, unknown>(svgRef.current);
          const currentTransform = d3.zoomTransform(svgElement.node() as SVGSVGElement);
          const isIdentity =
            Math.abs(currentTransform.k - 1) < 0.001 &&
            Math.abs(currentTransform.x) < 0.5 &&
            Math.abs(currentTransform.y) < 0.5;
          if (isIdentity) return;

          // Get container dimensions
          const containerWidth = svgContainerRef.current.clientWidth;
          const containerHeight = svgContainerRef.current.clientHeight;

          // Get SVG bounds
          const bounds = svgRef.current.getBBox();
          if (bounds.width <= 0 || bounds.height <= 0) return;

          // Calculate new scale to fit SVG in container while maintaining aspect ratio
          const scale = Math.min(
            containerWidth / bounds.width,
            containerHeight / bounds.height
          ) * 0.9; // 90% of container to add some padding

          // Create new transform that maintains current position but adjusts scale
          const newTransform = d3.zoomIdentity
            .translate(currentTransform.x, currentTransform.y)
            .scale(scale);

          // Apply transform with smooth transition
          svgElement
            .transition()
            .duration(200)
            .call(zoomRef.current.transform, newTransform);
        }
      }, 250); // Debounce for 250ms
    };

    globalThis.addEventListener('resize', handleResize);

    return () => {
      globalThis.removeEventListener('resize', handleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full min-h-[400px] w-full relative">
      <ZoomControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
        isLoading={isLoading}
        filename={filename}
        onPrev={onPrevImage}
        onNext={onNextImage}
        canGoPrev={canGoPrevImage}
        canGoNext={canGoNextImage}
        viewTabs={viewTabs}
        activeViewTabKey={activeViewTabKey}
        onViewTabChange={onViewTabChange}
      />
      <div className={`${isFileViewer ? '100vh' : ''}`}>
        {isLoading && <LoadingSpinner isFileViewer={isFileViewer} />}
      </div>

      <div className="relative w-full h-full">
        {svgLoadError ? (
          <div className="text-danger flex justify-center items-center h-[400px]">
            {svgLoadError}
          </div>
        ) : (
          <div className="relative w-full h-full">
            <button
              type="button"
              className="relative w-full h-full block cursor-default"
              onClick={() => tooltipData && setTooltipData(null)}
            >
              <div
                ref={svgContainerRef}
                className="relative w-full h-full flex items-center justify-center overflow-hidden bg-white"
                style={{
                  minHeight: '400px',
                  aspectRatio: '16/9',
                }}
              />
            </button>
            <TooltipComponent
              tooltipData={tooltipData}
              setTooltipData={setTooltipData}
              setSelectedRowId={setSelectedRowId}
              catalogSourceId={catalogSourceId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface FetchError {
  status: number;
  data: {
    message?: string;
  };
}

interface PartSvgViewerInlineProps {
  message: any;
  advisorId: string;
  currentActivePartsRowData: any;
  setSelectedRowId: any;
  onImagePartClick?: (componentDetail: any) => void;
  highlightedReferenceNumbers?: string[];
  partsRowSelectToken?: number;
}

// Extract SVG file handling logic into a custom hook
const useSvgFiles = (sourceIdData: any[], isPartsZip: boolean) => {
  return useMemo(() => {
    if (isPartsZip) {
      // One answer can cite several parts catalogs. Each component keeps the source_id of
      // the zip that owns it, so its diagram is fetched from that catalog.
      const fromAllCatalogs = sourceIdData.flatMap((source: any) => {
        if (source?.element_type !== 'parts_zip' || !Array.isArray(source.zip_components_details)) {
          return [];
        }
        return source.zip_components_details
          .filter((detail: any) => detail.component_file_name)
          .map((detail: any) => ({ ...detail, source_id: detail.source_id || source.source_id }));
      });
      if (fromAllCatalogs.length > 0) return fromAllCatalogs;
      if (sourceIdData[0]?.zip_components_details) {
        return sourceIdData[0].zip_components_details.filter(
          (source: any) => source.component_file_name
        );
      }
    }
    return sourceIdData.filter((source: any) => source.file_name);
  }, [sourceIdData, isPartsZip]);
};

// Extract file name and component name logic
const useFileNameHandlers = (isPartsZip: boolean) => {
  const getFileName = useCallback((source: any): string => {
    return isPartsZip
      ? source.component_file_name?.trim() ?? ''
      : source.file_name?.trim() ?? '';
  }, [isPartsZip]);

  const getComponentName = useCallback((source: any): string => {
    return isPartsZip
      ? source.component_name ?? source.component_file_name ?? ''
      : source.file_name ?? '';
  }, [isPartsZip]);

  return { getFileName, getComponentName };
};

// Extract SVG loading logic
const useSvgLoader = (
  svgFiles: any[],
  activeSvgIndex: number,
  svgCache: { [key: string]: string },
  setSvgCache: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
  coordinatesCache: { [key: string]: any[] },
  setCoordinatesCache: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>,
  componentDetailsCache: { [key: string]: any[] },
  setComponentDetailsCache: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>,
  parentCache: { [key: string]: any },
  setParentCache: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>,
  componentDiagramsCache: { [key: string]: any[] },
  setComponentDiagramsCache: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>,
  isPartsZip: boolean,
  sourceIdData: any[],
  advisorId: string,
  getFileName: (source: any) => string,
  getZipComponents: any,
  getPdfLink: any
) => {
  const [svgUrl, setSvgUrl] = useState<string>('');
  const [isPartsDataLoading, setIsPartsDataLoading] = useState(false);
  const [coordinates, setCoordinates] = useState<any[]>([]);
  const [componentDetails, setComponentDetails] = useState<any[]>([]);
  // The `component_group`-level diagram (e.g. "SEALER UNIT"), only present when the
  // backend returns `parent` (requested via `return_parent_component: true`).
  const [parentComponent, setParentComponent] = useState<any>(null);
  // The active component's own diagram list (a component can span multiple pages/
  // images, e.g. "Head Assembly G5iQ 230V - 2/3" backing the prev/next nav arrows).
  const [componentDiagrams, setComponentDiagrams] = useState<any[]>([]);
  const inFlightRequestsRef = useRef<Record<string, Promise<void>>>({});

  const loadSvg = useCallback(async () => {
    if (svgFiles.length === 0 || activeSvgIndex >= svgFiles.length) return;

    const activeSvg = svgFiles[activeSvgIndex];
    const activeFileName = getFileName(activeSvg);

    if (!activeFileName) {
      console.error('No filename available for the current SVG');
      return;
    }

    if (svgCache[activeFileName]) {
      setSvgUrl(svgCache[activeFileName]);
      setCoordinates(coordinatesCache[activeFileName] ?? []);
      setComponentDetails(componentDetailsCache[activeFileName] ?? []);
      setParentComponent(parentCache[activeFileName] ?? null);
      setComponentDiagrams(componentDiagramsCache[activeFileName] ?? []);
      return;
    }

    const inFlightRequest = inFlightRequestsRef.current[activeFileName];
    if (inFlightRequest) {
      try {
        setIsPartsDataLoading(true);
        await inFlightRequest;
      } finally {
        setIsPartsDataLoading(false);
      }
      return;
    }

    const fetchPromise = (async () => {
      if (isPartsZip) {
        const requestParams = {
          source_id: activeSvg.source_id || sourceIdData[0]?.source_id,
          componentGroup: activeSvg.component_group,
          componentName: activeSvg.component_name,
          filename: activeFileName
        };

        const response = await getZipComponents(requestParams).unwrap();
        const newUrl = response?.presigned_url ?? '';
        const newCoordinates = response?.coordinates ?? [];
        const newComponentDetails = response?.component_details ?? [];
        const newParent = response?.parent ?? null;
        const newComponentDiagrams = Array.isArray(response?.diagrams) ? response.diagrams : [];

        setSvgCache((prev: { [key: string]: string }) => ({ ...prev, [activeFileName]: newUrl }));
        setCoordinatesCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: newCoordinates }));
        setComponentDetailsCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: newComponentDetails }));
        setParentCache((prev: { [key: string]: any }) => ({ ...prev, [activeFileName]: newParent }));
        setComponentDiagramsCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: newComponentDiagrams }));

        setSvgUrl(newUrl);
        setCoordinates(newCoordinates);
        setComponentDetails(newComponentDetails);
        setParentComponent(newParent);
        setComponentDiagrams(newComponentDiagrams);
      } else {
        const elementRef = [
          { source_id: activeSvg.source_id, page_labels: [] },
        ];
        const getPdfLinkRequestPayload = {
          advisor_id: advisorId,
          source_ids: elementRef,
        };
        const response = await getPdfLink(getPdfLinkRequestPayload).unwrap();
        const newUrl = response?.data?.[0]?.full_source_presigned_url ?? '';
        const newCoordinates = response?.data?.[0]?.coordinates ?? [];
        const newComponentDetails = response?.data?.[0]?.component_details ?? [];

        setSvgCache((prev: { [key: string]: string }) => ({ ...prev, [activeFileName]: newUrl }));
        setCoordinatesCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: newCoordinates }));
        setComponentDetailsCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: newComponentDetails }));
        setComponentDiagramsCache((prev: { [key: string]: any[] }) => ({ ...prev, [activeFileName]: [] }));

        setSvgUrl(newUrl);
        setCoordinates(newCoordinates);
        setComponentDetails(newComponentDetails);
        setParentComponent(null);
        setComponentDiagrams([]);
      }
    })();

    inFlightRequestsRef.current[activeFileName] = fetchPromise;

    try {
      setIsPartsDataLoading(true);
      await fetchPromise;
    } catch (e) {
      console.error('Error fetching file:', e);
    } finally {
      delete inFlightRequestsRef.current[activeFileName];
      setIsPartsDataLoading(false);
    }
  }, [
    activeSvgIndex,
    svgFiles,
    getFileName,
    svgCache,
    setSvgCache,
    coordinatesCache,
    setCoordinatesCache,
    componentDetailsCache,
    setComponentDetailsCache,
    parentCache,
    setParentCache,
    componentDiagramsCache,
    setComponentDiagramsCache,
    sourceIdData,
    isPartsZip,
    advisorId,
    getPdfLink,
    getZipComponents
  ]);

  return { svgUrl, isPartsDataLoading, loadSvg, coordinates, componentDetails, parentComponent, componentDiagrams };
};

// Extract image handling logic
const useImageHandling = (
  setImageScale: React.Dispatch<React.SetStateAction<number>>
) => {
  const imgRef = useRef<any>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handleImageZoomIn = () => {
    setImageScale((prev: number) => Math.min(prev * 1.2, 5));
  };

  const handleImageZoomOut = () => {
    setImageScale((prev: number) => Math.max(prev * 0.8, 0.5));
  };

  const handleImageReset = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return {
    imgRef,
    imageContainerRef,
    imagePosition,
    isDragging,
    handleImageZoomIn,
    handleImageZoomOut,
    handleImageReset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  };
};

const PartSvgViewerInline: React.FC<PartSvgViewerInlineProps> = ({
  message,
  advisorId,
  currentActivePartsRowData,
  setSelectedRowId,
  onImagePartClick,
  highlightedReferenceNumbers,
  partsRowSelectToken,
}): JSX.Element => {
  const sourceIdData = message?.metaData?.[1]?.source_ids ?? [];
  const isPartsZip = sourceIdData.length > 0 && sourceIdData[0]?.element_type === 'parts_zip';

  const svgFiles = useSvgFiles(sourceIdData, isPartsZip);
  const [activeSvgIndex, setActiveSvgIndex] = useState(0);
  const [svgCache, setSvgCache] = useState<{ [fileName: string]: string }>({});
  const [coordinatesCache, setCoordinatesCache] = useState<{ [fileName: string]: any[] }>({});
  const [componentDetailsCache, setComponentDetailsCache] = useState<{ [fileName: string]: any[] }>({});
  const [parentCache, setParentCache] = useState<{ [fileName: string]: any }>({});
  const [componentDiagramsCache, setComponentDiagramsCache] = useState<{ [fileName: string]: any[] }>({});
  const [imageScale, setImageScale] = useState<number>(1);

  // "Component Group" vs "Component Name" toggle shown above the diagram, mirroring
  // the sample view-toggle: group tab shows the parent component-group's diagram,
  // component tab shows the currently selected component's own diagram (default).
  const [activeViewTab, setActiveViewTab] = useState<'group' | 'component'>('component');
  // Cache holds both the presigned URL and the hotspot coordinates for that page,
  // since `getHotspotView` (unlike the plain presigned-url-by-key lookup) returns both.
  const [groupImageCache, setGroupImageCache] = useState<{ [fileName: string]: { url: string; coordinates: any[] } }>({});
  const [isGroupImageLoading, setIsGroupImageLoading] = useState(false);
  // Which page of the parent group's own `diagrams` list is shown (Component Group tab).
  const [activeGroupDiagramIndex, setActiveGroupDiagramIndex] = useState(0);

  // Which page of the active component's own `diagrams` list is shown (Component Name
  // tab). Index 0 reuses the already-resolved `svgUrl`; later pages are fetched on demand.
  const [activeComponentDiagramIndex, setActiveComponentDiagramIndex] = useState(0);
  const [componentDiagramImageCache, setComponentDiagramImageCache] = useState<{ [fileName: string]: { url: string; coordinates: any[] } }>({});
  const [isComponentDiagramImageLoading, setIsComponentDiagramImageLoading] = useState(false);

  // API mutations for both types
  const [getZipComponents] = useGetPartsZipComponentDetailsMutation();
  const [getPdfLink, { error, isError }] = useGetPdfLinkForConversationsMutation();
  // Fetches presigned_url + hotspot coordinates together (unlike the plain by-key
  // lookup below), so prev/next diagram nav keeps hotspots working — same endpoint
  // BOM View's own prev/next nav uses (see partszipfileviewer.tsx's handleDiagramNavigate).
  const [getHotspotView] = useGetPartsHotspotViewMutation();

  const { getFileName, getComponentName } = useFileNameHandlers(isPartsZip);
  const { svgUrl, isPartsDataLoading, loadSvg, coordinates, componentDetails, parentComponent, componentDiagrams } = useSvgLoader(
    svgFiles,
    activeSvgIndex,
    svgCache,
    setSvgCache,
    coordinatesCache,
    setCoordinatesCache,
    componentDetailsCache,
    setComponentDetailsCache,
    parentCache,
    setParentCache,
    componentDiagramsCache,
    setComponentDiagramsCache,
    isPartsZip,
    sourceIdData,
    advisorId,
    getFileName,
    getZipComponents,
    getPdfLink
  );

  const catalogSourceId = svgFiles[activeSvgIndex]?.source_id
    || sourceIdData.find((source: any) => source?.element_type === 'parts_zip')?.source_id
    || sourceIdData[0]?.source_id;
  const groupDiagrams = parentComponent?.diagrams ?? [];
  const groupDiagram = groupDiagrams[activeGroupDiagramIndex];
  const groupTabDisabled = groupDiagrams.length === 0;
  const hasMultipleGroupDiagrams = groupDiagrams.length > 1;
  const canGoPrevGroupDiagram = activeGroupDiagramIndex > 0 && !isGroupImageLoading;
  const canGoNextGroupDiagram = activeGroupDiagramIndex < groupDiagrams.length - 1 && !isGroupImageLoading;
  const handlePrevGroupDiagram = () => setActiveGroupDiagramIndex((prev) => Math.max(prev - 1, 0));
  const handleNextGroupDiagram = () => setActiveGroupDiagramIndex((prev) => Math.min(prev + 1, groupDiagrams.length - 1));

  const hasMultipleComponentDiagrams = componentDiagrams.length > 1;
  const canGoPrevComponentDiagram = activeComponentDiagramIndex > 0 && !isComponentDiagramImageLoading;
  const canGoNextComponentDiagram = activeComponentDiagramIndex < componentDiagrams.length - 1 && !isComponentDiagramImageLoading;
  const handlePrevComponentDiagram = () => setActiveComponentDiagramIndex((prev) => Math.max(prev - 1, 0));
  const handleNextComponentDiagram = () => setActiveComponentDiagramIndex((prev) => Math.min(prev + 1, componentDiagrams.length - 1));

  // Rendered inline in the zoom-controls toolbar (matches the sample view-toggle).
  const viewTabs: ViewTabOption[] | undefined = isPartsZip
    ? [
        { key: 'group', label: 'Component Group', disabled: groupTabDisabled },
        { key: 'component', label: 'Component Name' },
      ]
    : undefined;
  const handleViewTabChange = (key: string) => setActiveViewTab(key as 'group' | 'component');

  // Reset to the component's own diagram whenever the active diagram changes,
  // since the group image belongs to whichever component is currently selected.
  useEffect(() => {
    setActiveViewTab('component');
    setActiveGroupDiagramIndex(0);
    setActiveComponentDiagramIndex(0);
  }, [activeSvgIndex]);

  // Fetch the parent component-group's diagram (presigned URL + hotspot coordinates)
  // on demand, only once the "Component Group" tab is actually selected, and cache it
  // per file name. Re-runs when the user pages through the group's own multi-diagram
  // nav. Uses the same hotspot-view endpoint as BOM View's prev/next nav (rather than
  // the plain presigned-url-by-key lookup) so hotspots keep working on these pages too.
  useEffect(() => {
    if (activeViewTab !== 'group') return;
    const groupFileName = groupDiagram?.file_name?.trim();
    const groupComponentName = parentComponent?.component_name?.trim();
    if (!groupFileName || !groupComponentName || !catalogSourceId) return;
    if (groupImageCache[groupFileName] !== undefined) return;

    let cancelled = false;
    setIsGroupImageLoading(true);

    getHotspotView({ source_id: catalogSourceId, component_name: groupComponentName, file_name: groupFileName })
      .unwrap()
      .then((response: any) => {
        if (cancelled) return;
        setGroupImageCache((prev) => ({
          ...prev,
          [groupFileName]: { url: response?.presigned_url ?? '', coordinates: response?.coordinates ?? [] },
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setGroupImageCache((prev) => ({ ...prev, [groupFileName]: { url: '', coordinates: [] } }));
        }
      })
      .finally(() => {
        if (!cancelled) setIsGroupImageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeViewTab, groupDiagram?.file_name, parentComponent?.component_name, catalogSourceId, groupImageCache, getHotspotView]);

  // Fetch the active component's own diagram (page beyond the first), presigned URL +
  // hotspot coordinates, on demand, only once the user navigates past index 0 via the
  // header nav arrows. Same hotspot-view endpoint as above and as BOM View's nav.
  useEffect(() => {
    if (activeViewTab !== 'component') return;
    if (activeComponentDiagramIndex === 0) return; // svgUrl already holds this page
    const targetFileName = componentDiagrams[activeComponentDiagramIndex]?.file_name?.trim();
    const targetComponentName = getComponentName(svgFiles[activeSvgIndex])?.trim();
    if (!targetFileName || !targetComponentName || !catalogSourceId) return;
    if (componentDiagramImageCache[targetFileName] !== undefined) return;

    let cancelled = false;
    setIsComponentDiagramImageLoading(true);

    getHotspotView({ source_id: catalogSourceId, component_name: targetComponentName, file_name: targetFileName })
      .unwrap()
      .then((response: any) => {
        if (cancelled) return;
        setComponentDiagramImageCache((prev) => ({
          ...prev,
          [targetFileName]: { url: response?.presigned_url ?? '', coordinates: response?.coordinates ?? [] },
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setComponentDiagramImageCache((prev) => ({ ...prev, [targetFileName]: { url: '', coordinates: [] } }));
        }
      })
      .finally(() => {
        if (!cancelled) setIsComponentDiagramImageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeViewTab, activeComponentDiagramIndex, componentDiagrams, svgFiles, activeSvgIndex, getComponentName, catalogSourceId, componentDiagramImageCache, getHotspotView]);

  const {
    imgRef,
    imageContainerRef,
    imagePosition,
    isDragging,
    handleImageZoomIn,
    handleImageZoomOut,
    handleImageReset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = useImageHandling(setImageScale);

  const handlePrev = () => {
    setActiveSvgIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setActiveSvgIndex((prev) => Math.min(prev + 1, svgFiles.length - 1));
  };

  const isSvgFile = useCallback((source: any): boolean => {
    const fileName = getFileName(source);
    return fileName.toLowerCase().endsWith('.svg');
  }, [getFileName]);

  const createActiveSourceData = useCallback((source: any) => {
    if (isPartsZip) {
      return {
        element_refs: [{
          text: source.text ?? []
        }]
      };
    }
    return source;
  }, [isPartsZip]);

  useEffect(() => {
    loadSvg();
  }, [loadSvg]);

  useEffect(() => {
    if (!currentActivePartsRowData) return;

    // Match the selected part's component against zip_components_details; the
    // answer table does not carry the component's diagram file name.
    const selectedPart = getSelectedPartRow(currentActivePartsRowData);
    const componentName = normPartField(selectedPart?.component_name);
    const componentGroup = normPartField(selectedPart?.component_group);
    if (!componentName) return;
    const nameIndex = svgFiles.findIndex(
      (source: any) => getComponentName(source) === componentName
    );
    // Group decides between catalogs that share a component name. A name-only match
    // remains the fallback, so a single catalog still resolves exactly as before.
    const groupedIndex = componentGroup
      ? svgFiles.findIndex((source: any) =>
          getComponentName(source) === componentName
          && String(source.component_group ?? '').trim() === componentGroup)
      : -1;
    const index = groupedIndex !== -1 ? groupedIndex : nameIndex;
    
    if (index !== -1 && index !== activeSvgIndex) {
      setActiveSvgIndex(index);
    }
  }, [currentActivePartsRowData, svgFiles, activeSvgIndex, getComponentName]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
      setImageScale((prev: number) => {
        const newScale = prev * scaleFactor;
        return Math.min(Math.max(newScale, 0.5), 5);
      });
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [svgUrl]);

  const renderContent = () => {
    if (!svgFiles[activeSvgIndex] || !svgUrl) {
      return (
        <NoDiagramPartTooltip
          message="No content available"
          currentActivePartsRowData={currentActivePartsRowData}
          isFileViewer={false}
          componentDetails={componentDetails}
          catalogSourceId={catalogSourceId}
          partsRowSelectToken={partsRowSelectToken}
        />
      );
    }

    const currentFile = svgFiles[activeSvgIndex];
    const isSvg = isSvgFile(currentFile);

    // Pages beyond the first in the component's own `diagrams` list, fetched via
    // hotspot-view so they keep the same hotspot coordinates/click behavior as index 0,
    // reusing the same header nav arrows so the user can page back to index 0's full view.
    if (activeComponentDiagramIndex > 0) {
      const targetFileName = componentDiagrams[activeComponentDiagramIndex]?.file_name?.trim();
      const diagramPage = targetFileName ? componentDiagramImageCache[targetFileName] : undefined;

      if (isComponentDiagramImageLoading && !diagramPage?.url) {
        return <LoadingSpinner isFileViewer={false} />;
      }

      if (!diagramPage?.url) {
        return (
          <NoDiagramPartTooltip
            currentActivePartsRowData={currentActivePartsRowData}
            isFileViewer={false}
            componentDetails={componentDetails}
            catalogSourceId={catalogSourceId}
            partsRowSelectToken={partsRowSelectToken}
          />
        );
      }

      return (
        <PartsImageViewer
          imageUrl={diagramPage.url}
          isFileViewer={false}
          currentActivePartsRowData={currentActivePartsRowData}
          setSelectedRowId={setSelectedRowId}
          filename={getComponentName(currentFile)}
          coordinates={diagramPage.coordinates}
          componentDetails={componentDetails}
          catalogSourceId={catalogSourceId}
          onImagePartClick={onImagePartClick}
          highlightedReferenceNumbers={highlightedReferenceNumbers}
          partsRowSelectToken={partsRowSelectToken}
          viewTabs={viewTabs}
          activeViewTabKey={activeViewTab}
          onViewTabChange={handleViewTabChange}
          onPrevImage={handlePrevComponentDiagram}
          onNextImage={handleNextComponentDiagram}
          canGoPrevImage={canGoPrevComponentDiagram}
          canGoNextImage={canGoNextComponentDiagram}
        />
      );
    }

    if (isSvg) {
      return (
        <PartsSvgViewer
          svgUrl={svgUrl}
          isFileViewer={false}
          currentActivePartsRowData={currentActivePartsRowData}
          setSelectedRowId={setSelectedRowId}
          activeSourceData={createActiveSourceData(currentFile)}
          filename={getComponentName(currentFile)}
          componentDetails={componentDetails}
          catalogSourceId={catalogSourceId}
          partsRowSelectToken={partsRowSelectToken}
          viewTabs={viewTabs}
          activeViewTabKey={activeViewTab}
          onViewTabChange={handleViewTabChange}
          onPrevImage={hasMultipleComponentDiagrams ? handlePrevComponentDiagram : undefined}
          onNextImage={hasMultipleComponentDiagrams ? handleNextComponentDiagram : undefined}
          canGoPrevImage={canGoPrevComponentDiagram}
          canGoNextImage={canGoNextComponentDiagram}
        />
      );
    }

    return (
      <PartsImageViewer
        imageUrl={svgUrl}
        isFileViewer={false}
        currentActivePartsRowData={currentActivePartsRowData}
        setSelectedRowId={setSelectedRowId}
        filename={getComponentName(currentFile)}
        coordinates={coordinates}
        componentDetails={componentDetails}
        catalogSourceId={catalogSourceId}
        onImagePartClick={onImagePartClick}
        highlightedReferenceNumbers={highlightedReferenceNumbers}
        partsRowSelectToken={partsRowSelectToken}
        viewTabs={viewTabs}
        activeViewTabKey={activeViewTab}
        onViewTabChange={handleViewTabChange}
        onPrevImage={hasMultipleComponentDiagrams ? handlePrevComponentDiagram : undefined}
        onNextImage={hasMultipleComponentDiagrams ? handleNextComponentDiagram : undefined}
        canGoPrevImage={canGoPrevComponentDiagram}
        canGoNextImage={canGoNextComponentDiagram}
      />
    );
  };

  // "Component Group" tab content: the parent component-group's own diagram
  // (e.g. "SEALER UNIT"), resolved on demand via hotspot-view so its hotspot
  // coordinates come back too. The group can itself have multiple diagrams,
  // backing the same header nav arrows.
  const renderGroupContent = () => {
    const groupFileName = groupDiagram?.file_name?.trim();

    if (!groupFileName) {
      return (
        <div className="flex justify-center items-center h-full text-default-500">
          No component group image available.
        </div>
      );
    }

    const groupPage = groupImageCache[groupFileName];

    if (isGroupImageLoading && !groupPage?.url) {
      return <LoadingSpinner isFileViewer={false} />;
    }

    if (!groupPage?.url) {
      return (
        <div className="flex justify-center items-center h-full text-default-500">
          No component group image available.
        </div>
      );
    }

    return (
      <PartsImageViewer
        imageUrl={groupPage.url}
        isFileViewer={false}
        currentActivePartsRowData={currentActivePartsRowData}
        setSelectedRowId={setSelectedRowId}
        filename={parentComponent?.component_name}
        coordinates={groupPage.coordinates}
        componentDetails={componentDetails}
        catalogSourceId={catalogSourceId}
        onImagePartClick={onImagePartClick}
        highlightedReferenceNumbers={highlightedReferenceNumbers}
        partsRowSelectToken={partsRowSelectToken}
        viewTabs={viewTabs}
        activeViewTabKey={activeViewTab}
        onViewTabChange={handleViewTabChange}
        onPrevImage={hasMultipleGroupDiagrams ? handlePrevGroupDiagram : undefined}
        onNextImage={hasMultipleGroupDiagrams ? handleNextGroupDiagram : undefined}
        canGoPrevImage={canGoPrevGroupDiagram}
        canGoNextImage={canGoNextGroupDiagram}
      />
    );
  };

  return (
    <div className="flex flex-col h-[500px] relative w-full min-w-0 max-w-full overflow-hidden">
      {svgFiles?.length > 1 && (
        <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none z-10">
          {/* <button
            onClick={handlePrev}
            disabled={activeSvgIndex === 0}
            className={`btn-prev pointer-events-auto ${activeSvgIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Previous"
            aria-label="Previous SVG"
            tabIndex={0}
          >
            <Icon
              icon="mingcute:left-fill"
              width="24"
              height="24"
              style={{ color: 'blue' }}
            />
          </button>
          <button
            onClick={handleNext}
            disabled={activeSvgIndex === svgFiles.length - 1}
            className={`btn-next pointer-events-auto ${activeSvgIndex === svgFiles.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Next"
            aria-label="Next SVG"
            tabIndex={0}
          >
            <Icon
              icon="mingcute:right-fill"
              width="24"
              height="24"
              style={{ color: 'blue' }}
            />
          </button> */}
        </div>
      )}

      {svgFiles.length > 0 ? (
        <div className="w-full relative flex-1 min-h-0">
          {isError ? (
            <div className="text-danger flex justify-center items-center h-full">
              {(error as FetchError)?.data?.message ?? 'Failed to load SVG, please try again later.'}
            </div>
          ) : isPartsDataLoading ? (
            <LoadingSpinner isFileViewer={false} />
          ) : (
            <div className="w-[100%] h-[100%]">
              {activeViewTab === 'group' ? renderGroupContent() : renderContent()}
            </div>
          )}
        </div>
      ) : (
        <NoDiagramPartTooltip
          currentActivePartsRowData={currentActivePartsRowData}
          isFileViewer={false}
          componentDetails={componentDetails}
          catalogSourceId={catalogSourceId}
          partsRowSelectToken={partsRowSelectToken}
        />
      )}
    </div>
  );
};

interface TooltipComponentProps {
  tooltipData: PartInfo | null;
  setTooltipData: (data: PartInfo | null) => void;
  setSelectedRowId?: (id: string) => void;
  catalogSourceId?: string;
}

const TOOLTIP_MARGIN = 8;
const PART_IMAGE_HEIGHT = 160;

/**
 * In-flight and resolved part-image lookups, keyed by S3 key.
 *
 * The presign is a mutation, so it is not deduped or cached by RTK Query: without
 * this, one tooltip fires the request once per effect run (twice under StrictMode's
 * double-invoke) and again every time the same part is reopened. Sharing the
 * promise collapses those into a single `by-key` call per part.
 */
const partImageUrlCache = new Map<string, Promise<string | null>>();

const PartTooltipImage = ({
  catalogSourceId,
  fileName,
}: {
  catalogSourceId?: string;
  fileName?: string;
}) => {
  const { t } = useTranslation();
  const [getPresignedUrlByKey] = useGetEvaluationsPresignedUrlMutation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!catalogSourceId || !fileName) {
      setImageUrl(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setImageUrl(null);

    const s3Key = buildPartCatalogS3Key(catalogSourceId, fileName);

    const presign = (key: string) =>
      getPresignedUrlByKey({ s3_key: key })
        .unwrap()
        .then((response) => response?.data?.presigned_url ?? null)
        .catch(() => null);

    let request = partImageUrlCache.get(s3Key);
    if (!request) {
      request = presign(s3Key)
        .then((url) => {
          if (url) return url;
          // The name as given didn't resolve. If it carried a directory prefix
          // ("images/row3_39.jpeg"), retry with just the file name — the object
          // sits directly under the catalog prefix on some screens.
          const bareName = toPartImageFileNameOnly(fileName);
          if (bareName === fileName) return null;
          return presign(buildPartCatalogS3Key(catalogSourceId, bareName));
        })
        .then((url) => {
          // Don't cache a miss: a later open should be able to retry.
          if (!url) partImageUrlCache.delete(s3Key);
          return url;
        });
      partImageUrlCache.set(s3Key, request);
    }

    void request
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogSourceId, fileName, getPresignedUrlByKey]);

  // Nothing to look up, so no image section at all.
  if (!catalogSourceId || !fileName) {
    return null;
  }

  // The part has no image (or it failed to resolve): render nothing rather than an
  // empty placeholder, so the tooltip stays compact.
  if (!isLoading && !imageUrl) {
    return null;
  }

  return (
    <div
      className="w-full rounded-md bg-default-50 overflow-hidden"
      style={{ height: PART_IMAGE_HEIGHT }}
    >
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <CircularProgress size="sm" aria-label={t('Loading part image')} />
        </div>
      ) : (
        <img
          src={imageUrl ?? ''}
          alt={t('Part image')}
          className="block h-full w-full object-contain object-center"
          onError={() => {
            // A cached presigned URL can outlive its expiry — drop it so the next
            // open re-signs instead of reusing the dead URL. Clearing the URL also
            // collapses this section, since there is no image to show.
            partImageUrlCache.delete(
              buildPartCatalogS3Key(catalogSourceId, fileName)
            );
            setImageUrl(null);
          }}
        />
      )}
    </div>
  );
};

const TooltipComponent = ({
  tooltipData,
  setTooltipData,
  setSelectedRowId,
  catalogSourceId,
}: TooltipComponentProps) => {
  const rootRef = useRef<HTMLDialogElement>(null);
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const [localAnchorClient, setLocalAnchorClient] = useState<{
    cx: number;
    cy: number;
  } | null>(null);

  const handleCloseTooltip = useCallback(() => {
    setTooltipData(null);
    setSelectedRowId?.('');
  }, [setTooltipData, setSelectedRowId]);

  const hasViewportAnchor =
    tooltipData?.anchorClientX != null &&
    tooltipData?.anchorClientY != null;
  const hasLocalOffset =
    tooltipData?.x != null &&
    tooltipData?.y != null &&
    !hasViewportAnchor;

  useLayoutEffect(() => {
    if (!tooltipData) {
      setLocalAnchorClient(null);
      return;
    }
    if (hasViewportAnchor) {
      setLocalAnchorClient(null);
      return;
    }
    if (!hasLocalOffset) {
      setLocalAnchorClient(null);
      return;
    }
    const el = sentinelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    setLocalAnchorClient((prev) =>
      prev && Math.abs(prev.cx - cx) < 0.5 && Math.abs(prev.cy - cy) < 0.5
        ? prev
        : { cx, cy }
    );
  }, [
    tooltipData?.parts_number,
    tooltipData?.description,
    tooltipData?.x,
    tooltipData?.y,
    tooltipData?.anchorClientX,
    tooltipData?.anchorClientY,
    hasViewportAnchor,
    hasLocalOffset,
  ]);

  useEffect(() => {
    if (!tooltipData || hasViewportAnchor || !hasLocalOffset) return;
    const run = () => {
      const el = sentinelRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      setLocalAnchorClient((prev) =>
        prev && Math.abs(prev.cx - cx) < 0.5 && Math.abs(prev.cy - cy) < 0.5
          ? prev
          : { cx, cy }
      );
    };
    globalThis.addEventListener('scroll', run, true);
    globalThis.addEventListener('resize', run);
    const id = requestAnimationFrame(run);
    return () => {
      globalThis.removeEventListener('scroll', run, true);
      globalThis.removeEventListener('resize', run);
      cancelAnimationFrame(id);
    };
  }, [tooltipData, hasViewportAnchor, hasLocalOffset]);

  useEffect(() => {
    if (!tooltipData) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseTooltip();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [tooltipData, handleCloseTooltip]);

  useEffect(() => {
    if (!tooltipData) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        handleCloseTooltip();
      }
    };
    document.addEventListener('pointerdown', onDown, false);
    return () => document.removeEventListener('pointerdown', onDown, false);
  }, [tooltipData, handleCloseTooltip]);

  if (!tooltipData) {
    return null;
  }
  if (!hasViewportAnchor && !hasLocalOffset) {
    return null;
  }

  const cx = hasViewportAnchor
    ? tooltipData.anchorClientX!
    : localAnchorClient?.cx;
  const cy = hasViewportAnchor
    ? tooltipData.anchorClientY!
    : localAnchorClient?.cy;

  if (cx == null || cy == null) {
    return (
      <span
        ref={sentinelRef}
        className="pointer-events-none absolute h-0 w-0"
        style={{ left: tooltipData.x, top: tooltipData.y }}
        aria-hidden
      />
    );
  }

  const clampedLeft = Math.min(
    globalThis.innerWidth - TOOLTIP_MARGIN,
    Math.max(TOOLTIP_MARGIN, cx)
  );
  const ay = cy;

  const portal = (
    <dialog
      open
      ref={rootRef}
      className="fixed z-[99999] pointer-events-none bg-transparent border-0 p-0 m-0"
      style={{ left: clampedLeft, top: ay, width: 0, height: 0 }}
      aria-modal="true"
    >
      <div className="pointer-events-auto absolute bottom-full left-1/2 mb-2 w-[min(360px,calc(100vw-16px))] max-w-[360px] -translate-x-1/2 flex flex-col items-center">
        <Card className="relative z-0 shadow-xl border border-default-200 bg-background w-full">
          <CardHeader className="flex justify-between items-center gap-2 pb-1">
            <h3 className="font-semibold text-md truncate">
              Part #: {tooltipData.parts_number}
            </h3>
            <button
              type="button"
              className="text-default-700 hover:text-foreground rounded-full p-1 shrink-0"
              onClick={handleCloseTooltip}
              title="Close"
            >
              <Icon icon="mdi:close" width="20" height="20" />
            </button>
          </CardHeader>
          <CardBody className="pt-0 gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-default-500">Description</p>
              <p className="text-sm text-primary font-semibold italic">
                {tooltipData.description}
              </p>
            </div>
            <PartTooltipImage
              catalogSourceId={catalogSourceId}
              fileName={tooltipData.part_file_name}
            />
            <div className="flex justify-end pt-1">
              <CartActions
                partNumber={tooltipData.parts_number}
                partName={tooltipData.description}
                referenceNumber={
                  tooltipData.ref_number?.trim()
                    ? tooltipData.ref_number
                    : tooltipData.parts_number
                }
                onSuccessCallback={handleCloseTooltip}
              />
            </div>
          </CardBody>
        </Card>
        {/* Tip fill tracks the card surface (bg-background); z-index + drop-shadow so the tip is not hidden under the card shadow. */}
        <div
          className="relative z-10 box-border h-0 w-0 border-solid border-x-8 border-x-transparent border-t-8 border-t-background drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]"
          aria-hidden
        />
      </div>
    </dialog>
  );

  return (
    <>
      {hasLocalOffset ? (
        <span
          ref={sentinelRef}
          className="pointer-events-none absolute h-0 w-0"
          style={{ left: tooltipData.x, top: tooltipData.y }}
          aria-hidden
        />
      ) : null}
      {createPortal(portal, document.body)}
    </>
  );
};

/**
 * Placeholder shown when the selected component has no diagram (or the diagram
 * has no hotspots to anchor to). The part-level image can still exist, so the
 * tooltip is rendered anyway, anchored to the centre of this panel.
 */
const NoDiagramPartTooltip = ({
  message,
  currentActivePartsRowData,
  isFileViewer,
  componentDetails,
  catalogSourceId,
  partsRowSelectToken,
  setSelectedRowId,
}: {
  message?: React.ReactNode;
  currentActivePartsRowData?: any;
  isFileViewer?: boolean;
  componentDetails?: any[];
  catalogSourceId?: string;
  partsRowSelectToken?: number;
  setSelectedRowId?: any;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<PartInfo | null>(null);

  const partInfo = useMemo(
    () =>
      buildPartInfoFromRowData(currentActivePartsRowData, componentDetails),
    [currentActivePartsRowData, componentDetails]
  );

  useEffect(() => {
    if (!partInfo) {
      setTooltipData(null);
      return;
    }

    const place = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const anchorClientX = rect.left + rect.width / 2;
      const anchorClientY = rect.top + rect.height / 2;
      setTooltipData((prev) => {
        // Bail out when nothing moved: `place` runs on every scroll/resize, and
        // returning a fresh object each time would re-render (and on hosts that
        // rebuild the row-data array, re-run this effect) in a loop.
        if (
          prev?.parts_number === partInfo.parts_number &&
          prev?.ref_number === partInfo.ref_number &&
          prev?.part_file_name === partInfo.part_file_name &&
          Math.abs((prev.anchorClientX ?? 0) - anchorClientX) < 0.5 &&
          Math.abs((prev.anchorClientY ?? 0) - anchorClientY) < 0.5
        ) {
          return prev;
        }
        return { ...partInfo, anchorClientX, anchorClientY };
      });
    };

    const raf = requestAnimationFrame(place);
    globalThis.addEventListener('resize', place);
    globalThis.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      globalThis.removeEventListener('resize', place);
      globalThis.removeEventListener('scroll', place, true);
    };
  }, [partInfo, partsRowSelectToken]);

  return (
    <div
      ref={containerRef}
      className="relative flex justify-center items-center h-full w-full text-default-500"
    >
      {message ?? 'No image available.'}
      <TooltipComponent
        tooltipData={tooltipData}
        setTooltipData={setTooltipData}
        setSelectedRowId={isFileViewer ? setSelectedRowId : undefined}
        catalogSourceId={catalogSourceId}
      />
    </div>
  );
};

export { PartSvgViewerInline, PartsSvgViewer, TooltipComponent, NoDiagramPartTooltip };